"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { PlusIcon } from "@/components/icons";
import { SidebarHistory } from "@/components/sidebar-history";
import { SidebarUserNav } from "@/components/sidebar-user-nav";
import { SearchChatHistoryPopup } from "@/components/SearchChatPopup";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  useSidebar, SidebarGroupLabel, SidebarGroupContent, SidebarGroup
} from "@/components/ui/sidebar";
import { useArtifact } from "@/hooks/use-artifact";
import Link from "next/link";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import Image from "next/image";
import Logo from "@/public/images/chatbot.png";
import BlackLogo from "@/public/images/chatbot-black.png";
import { SidebarToggle } from "./sidebar-toggle";
import * as LucideIcons from "lucide-react";
import { Search, Compass, FlaskConical, Folder, ChevronDown, ChevronUp, FolderPlus, Layout, Paintbrush, Gift } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import {
  setHtmlContentNew,
  setGlobalLoading,
  setProjects,
  setPublicWithChats,
  setActiveItem,
  setUserData,
  setActiveTab,
} from "@/store/mainSlice";
import CategoryMoreMenu from "./CategoryMoreMenu"
import UpgradePlan from "./upgrade-plan";

export function AppSidebar({ user, onProjectsClick }) {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useDispatch();
  const { setOpenMobile,setOpen, state } = useSidebar();
  const { setArtifact } = useArtifact();
  const { isGeneratingWebsite, activeItem, activeTab, userData } = useSelector(
    (state) => state.main
  );
  const projects = useSelector((state) => state.main.projects || []);
  const publicWithChats = useSelector(
  state => state.main.publicWithChats || []
);
  const [container, setContainer] = useState("");
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
const [openShortcut, setOpenShortcut] = useState(activeTab || null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [labLoading, setLabLoading] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [publicProjects, setPublicProjects] = useState([]);
  const [openPublicProjects, setOpenPublicProjects] = useState(false);
  const isExploreActive = pathname === "/explore";
  const isLabActive = pathname === "/explore-agents";
  const isProjectPage = pathname.startsWith("/project");
  const hideActionButtons = isProjectPage;
  const userIdParent = userData._id || user?._id
  const isCollapsed = state === "icon";
const isSubscribed =
  subscriptionStatus === "active" ||
  userData?.subscriptionStatus === "active";

  useEffect(() => {
    const handler = () => {
      setShowDropdown(true);  
    };
    window.addEventListener("refresh-projects", handler);
    return () => window.removeEventListener("refresh-projects", handler);
  }, []);
  useEffect(() => {
  const stored = localStorage.getItem("publicWithChats");
  if (stored) {
    dispatch(setPublicWithChats(JSON.parse(stored)));
  }
}, []);


  useEffect(() => {
    setContainer(document.body);
  }, []);

  useEffect(() => {
    if (isCollapsed) {
      setShowDropdown(false);
      setOpen(true); 
    }
  }, [isCollapsed]);

  useEffect(() => {
  if (activeTab) {
    setOpenShortcut(activeTab);
  }
}, [activeTab]);

  useEffect(() => {
    const handleRedirectProject = async (e) => {
      const { projectId } = e.detail;
        dispatch(setActiveTab("Chat"));
      setIsRedirecting(true); // start loader
      setShowDropdown(true);  // keep dropdown open

      try {
        await router.push(`/project/${projectId}`);
      } finally {
        setIsRedirecting(false); // redirect done
      }
    };

    window.addEventListener("project-redirect", handleRedirectProject);
    return () => window.removeEventListener("project-redirect", handleRedirectProject);
  }, [router, dispatch]);


useEffect(() => {
  if (pathname.startsWith("/project/")) {
    const segments = pathname.split("/").filter(Boolean);
    const currentProjectId = segments[1];
    const isPublic = publicProjects?.some(
      (p) => p.slug === currentProjectId || p.parent_scr_id === currentProjectId
    );
    const currentProject = projects?.find(
      (p) => p.slug === currentProjectId || p.parent_scr_id === currentProjectId
    );

    if (isPublic || currentProject) {
      setShowDropdown(true);
      return;
    }
  }

  setShowDropdown(false);
}, [pathname, projects, publicProjects, isRedirecting]);

 
  useEffect(() => {
    async function fetchProjects() {
      if (!userIdParent) return;
      let allProjects = [];
      const res = await fetch(`${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/project/list?user_id=${userIdParent}`);
      const resLimited = await fetch(`${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/chatbots/limited/list?user_id=${userIdParent}`);

      const data = await res.json();
      const limitedData = await resLimited.json();
  
      if (data.status === 1 && Array.isArray(data.projects)) {
        allProjects = [...data.projects];
      }

       if (limitedData.status === 1 && Array.isArray(limitedData.data)) {
        allProjects = [...allProjects,...limitedData.data];
      }
       dispatch(setProjects(allProjects));
    }
    fetchProjects();
  }, [userIdParent, dispatch]);


  // 🔐 Fetch subscription status so we can gate project creation
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        if (!userIdParent) return;
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_APP_SUBSCRIPTION_STATUS}?userId=${userIdParent}`
        );
        const data = await res.json();
        setSubscriptionStatus(data.status || null);
      } catch (err) {
        console.error("Failed to fetch subscription status", err);
        setSubscriptionStatus(null);
      }
    };

    fetchStatus();
  }, [userIdParent]);
  
  // Listen for subscription status updates
  useEffect(() => {
    const handleSubscriptionUpdate = (e) => {
      const { status, userId } = e.detail;
      // Only update if it's for the current user
      if (status === "active" && (!userId || userId === userIdParent)) {
        setSubscriptionStatus("active");
        // Also update userData in Redux if available
        if (userData?._id === userIdParent) {
          dispatch(setUserData({
            ...userData,
            subscriptionStatus: "active"
          }));
        }
      }
    };

    window.addEventListener("subscription-updated", handleSubscriptionUpdate);
    return () => {
      window.removeEventListener("subscription-updated", handleSubscriptionUpdate);
    };
  }, [userIdParent, userData, dispatch]);

  useEffect(() => {
    const fetchPublicChatbots = async () => {
      try {
        const res = await fetch(`${process.env.NEXT_PUBLIC_CHATBOT_PAGE}/public/list`, {
          method: "GET",
        });
        const data = await res.json();
        if (data.status === 1 && Array.isArray(data.data)) {
          setPublicProjects(data.data);
        }
      } catch (err) {
        console.error("Error fetching chatbots", err);
      }
    };
    fetchPublicChatbots();
  }, []);

  useEffect(() => {
    const handleProjectCreated = (e) => {
      const { parentScrId, project } = e.detail;

      setShowDropdown(true);

      const newList = [...projects];
      const exists = newList.some(
        (p) => p.parent_scr_id === project.parent_scr_id
      );

      if (!exists) {
        newList.push(project);
      }

      dispatch(setProjects(newList));
    }
    window.addEventListener("project-created", handleProjectCreated);
    return () => window.removeEventListener("project-created", handleProjectCreated);
  }, [projects]);

  const handleRedirect = async (e) => {
    e.preventDefault();
    if (isGeneratingWebsite) {
      toast.error("Please hold on—we’re setting up your page.", {
        style: { color: "red" },
        position: "top-center",
        iconTheme: { primary: "red", secondary: "#fff" },
      });
      return;
    }
    // If already on /explore, do nothing (no redirect, no blur)
    if (isExploreActive) {
      setOpenMobile?.(false);
      return;
    }
    setArtifact((prev) => (
      {
        ...prev,
        isVisible: false
      }
    ))
    dispatch(setGlobalLoading(true))
    setLoading(true);
    setOpenMobile?.(false);

    setTimeout(async () => {
      await router.push("/explore");
    }, 150);

  };

  const handleProjectsButtonClick = () => {
    // ✅ Only treat user as subscribed if backend / Redux says so
    const isSubscribed =
      subscriptionStatus === "active" || userData?.subscriptionStatus === "active";

    if (isSubscribed) {
      // If subscription active → proceed as usual
      onProjectsClick?.();
      return;
    }

    // Otherwise open Upgrade Plan popup
    dispatch(setActiveItem("z-[45]"));
    setShowPlan(true);
  };

//   useEffect(() => {
//   const status = localStorage.getItem("subscriptionStatus");
//   if (status === "active") {
//     setShowPlan(false);
//   }
// }, []);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  if (!hasMounted) return null;

  const handleLabRedirect = async (e) => {
    e.preventDefault();
    if (isGeneratingWebsite) {
      toast.error("Please hold on—we’re setting up your page.", {
        style: { color: "red" },
        position: "top-center",
        iconTheme: { primary: "red", secondary: "#fff" },
      });
      return;
    }
    // If already on /explore, do nothing (no redirect, no blur)
    if (isLabActive) {
      setOpenMobile?.(false);
      return;
    }
    dispatch(setGlobalLoading(true))
    setLabLoading(true);
    setTimeout(async () => {
      await router.push("/explore-agents");
    }, 150);
  };

  // const handleAgentShortcutClick = async (tab, route = "/") => {
  //   if (isGeneratingWebsite) {
  //     toast.error("Please hold on—we’re setting up your page.", {
  //       style: { color: "red" },
  //       position: "top-center",
  //       iconTheme: { primary: "red", secondary: "#fff" },
  //     });
  //     return;
  //   }
  //   dispatch(setActiveTab(tab));
  //   setOpenMobile?.(false);
  //   await router.push(route);
  // };
const handleAgentShortcutClick = async (tab, route = "/") => {
  if (isGeneratingWebsite) {
    toast.error("Please hold on—we’re setting up your page.", {
      style: { color: "red" },
      position: "top-center",
      iconTheme: { primary: "red", secondary: "#fff" },
    });
    return;
  }

  // ✅ If sidebar collapsed → expand it
  if (isCollapsed) {
    setOpenMobile?.(false); // mobile close
    dispatch(setActiveItem("")); // optional reset
  }

  // ✅ Close artifact (VERY IMPORTANT for your case)
  setArtifact((prev) => ({
    ...prev,
    isVisible: false,
  }));

  // ✅ Set active tab
  dispatch(setActiveTab(tab));

  // ✅ Open dropdown (after expand)
  setOpenShortcut(tab);

  // ✅ Navigate
  if (route) {
    await router.push(route);
  }
};

  const agentShortcuts = [
    {
      id: "web-page-builder",
      label: "Web Page Builder",
      tab: "Page Builder",
      icon: <Layout size={16} />,
      route: "/",
    },
    {
      id: "greeting",
      label: "Greeting Card Maker",
      tab: "Card Maker",
      icon: <Gift size={16} />,
      route: "/",
    },
  ];

  const getButtonText = (action) => {
    let baseText;

    if (!activeTab) {
      return "New Chat";
    }
    if (activeTab === "Card Maker") {
      baseText = "Card";
    } else if (activeTab === "Page Builder") {
      baseText = "Web Page";
    } else {
      baseText = activeTab;
    }
    return `${action} ${baseText}`;
  };

  const publicProjectsWithChats = publicProjects.filter((p) =>
  publicWithChats.includes(p.parent_scr_id)
);

const hasProjects =
 projects.length > 0 || publicProjectsWithChats.length > 0;

  return (
    <>
      <Sidebar className="" activeItem={activeItem}>
        <SidebarToggle className="absolute top-[8px] right-[8px] group-data-[collapsible=offcanvas]:hover:opacity-100" />

        <SidebarHeader className="">
          <SidebarMenu>
            <div className="flex flex-col justify-between items-start gap-2">
              <div className="flex items-center flex-wrap w-full">
                <Link
                  href="/"
                  onClick={() => {
                    setOpenMobile(false);
                  }}
                  className="flex flex-row gap-3 items-center rounded-2 w-[32px] h-[32px] py-1.5 px-1 hover:bg-accent rounded-lg group-data-[collapsible=offcanvas]:block group-data-[collapsible=offcanvas]:opacity-0 hidden"
                >
                  <Image
                    src={Logo}
                    className="w-[24px] h-[20px] object-contain hidden dark:block"
                    alt=""
                  />
                  <Image
                    src={BlackLogo}
                    className="w-[24px] h-[20px] object-contain block dark:hidden"
                    alt=""
                  />
                </Link>
                {/* <span className="text-lg font-semibold px-2 hover:bg-muted rounded-md cursor-pointer group-data-[collapsible=offcanvas]:hidden block">
                  Varnz.AI
                </span> */}
                <Link
                  href="/explore-agents"
                  onClick={() => setOpenMobile(false)}
                  className="text-lg font-semibold px-2 hover:bg-muted rounded-md cursor-pointer group-data-[collapsible=offcanvas]:hidden block"
                >
                  Varnz.AI
                </Link>
              </div>
              <Tooltip>
                <TooltipTrigger asChild></TooltipTrigger>
                <TooltipContent
                  align="start"
                  sideOffset={8}
                  container={container ?? undefined}
                >
                  New Chat
                </TooltipContent>
              </Tooltip>

              <Button variant="ghost" type="button" className="p-2 h-fit w-full justify-start dark:hover:bg-sidebar-accent"
                onClick={handleLabRedirect}
                disabled={labLoading}
              >
                <div className="flex items-center">
                  <FlaskConical />
                  <span className="ml-2 leading-[1] group-data-[collapsible=icon]:hidden group-data-[collapsible=offcanvas]:hidden">
                    Explore Agents
                  </span>
                </div>
              </Button>

                          
              {/* {isSubscribed && ( */}
                {/* <> */}
                  {/* Projects List */}
                  {/* {hasProjects && !isCollapsed && (
                    <div className="space-y-1 w-full sidebar-project-dropdown ">
                      <CategoryMoreMenu publicProjects={publicProjects} />
                    </div>
                  )} */}
              {hasProjects && (
                <div className="space-y-1 sidebar-project-dropdown w-full">
                  <CategoryMoreMenu publicProjects={publicProjects} isCollapsed={isCollapsed} />
                </div>
              )}
                  {/* {agentShortcuts.map((item) => (
                  <div key={item.id} className="w-full">
                    <Button
                      variant="ghost"
                      type="button"
                      className={`p-2 h-fit w-full justify-start dark:hover:bg-sidebar-accent group-data-[collapsible=icon]:justify-center group-data-[collapsible=offcanvas]:justify-center ${activeTab === item.tab ? "bg-sidebar-accent" : ""
                        }`}
                      onClick={() => handleAgentShortcutClick(item.tab, item.route)}
                    >
                      <div className="flex items-center">
                        {item.icon}
                        <span className="ml-2 leading-[1] group-data-[collapsible=icon]:hidden group-data-[collapsible=offcanvas]:hidden">
                          {item.label}
                        </span>
                      </div>
                    </Button>
                    {activeTab === item.tab && (
                      <SidebarHistory
                        forcedTab={item.tab}
                        showEmptyState={true}
                        className="mt-1 ml-6 p-0 group-data-[collapsible=icon]:hidden group-data-[collapsible=offcanvas]:hidden"
                      />
                    )}
                  </div>
                ))} */}

{agentShortcuts.map((item) => {
  const isOpen = openShortcut === item.tab;

  return (
    <div key={item.id} className="w-full">
      <Button
        variant="ghost"
        type="button"
        className={`p-2 h-fit w-full justify-start dark:hover:bg-sidebar-accent 
        group-data-[collapsible=icon]:justify-center 
        group-data-[collapsible=offcanvas]:justify-center 
        ${isOpen ? "bg-sidebar-accent" : ""}`}
      onClick={() => handleAgentShortcutClick(item.tab, item.route)}
      >
        <div className="flex items-center">
          {item.icon}
          <span className="ml-2 leading-[1] 
          group-data-[collapsible=icon]:hidden 
          group-data-[collapsible=offcanvas]:hidden">
            {item.label}
          </span>
        </div>
      </Button>

      {isOpen && (
        <SidebarHistory
          forcedTab={item.tab}
          showEmptyState={true}
          className="mt-1 ml-6 p-0 max-h-[250px] overflow-y-auto 
          group-data-[collapsible=icon]:hidden 
          group-data-[collapsible=offcanvas]:hidden"
        />
      )}
    </div>
  );
})}
            </div>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarFooter className="shrink-0">
          <SidebarUserNav />
        </SidebarFooter>
      </Sidebar>
      <SearchChatHistoryPopup
        isOpen={showSearchPopup}
        onClose={() => setShowSearchPopup(false)}
      />
      {showPlan && userData && (
        <UpgradePlan
          show={showPlan}
          handleClose={() => setShowPlan(false)}
          userDetail={userData}
        />
      )}
    </>
  );
}
